import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DesktopApp, DesktopConfig, MailConfig } from "../scenario/types";
import { Desktop } from "./Desktop";
import { hubBackHref } from "../lib/hostConfig";
import { Mail } from "./Mail";
import {
  applyShare,
  approveApp,
  clearDirectShares,
  gradeMission,
  hasAnyoneLink,
  initialWorld,
  moveScheduleToProject,
  removeGrant,
  removeGuest,
  removeLink,
  reportApp,
  revokeApp,
  roleOf,
  type AccessRow,
  type Grade,
  type World,
} from "./sendItEngine";
import { AccessWin } from "./sendit/AccessWin";
import { AppsWin, appStatus } from "./sendit/AppsWin";
import {
  APP_REQUESTS,
  MISSIONS,
  fileOf,
  personOf,
  placeOf,
  type FileId,
  type Mission,
  type MissionId,
} from "./sendit/content";
import { FilesWin } from "./sendit/FilesWin";
import { Ic, type IcName } from "./sendit/icons";
import { GuideBody, HelpBody, ViewerBody } from "./sendit/InfoWins";
import { ResultBody, SummaryBody } from "./sendit/ResultWin";
import { ShareDialog, type ShareDraft } from "./sendit/ShareDialog";
import { SisWin, activeWinId, useCompact, useWindows, type WinId, type WinMeta } from "./sendit/Win";

/** What the top-right guide card should say. App.tsx renders it; this file owns the words. */
export interface SendItGuide {
  step: string;
  title: string;
  body: string;
  /** Exact on-screen control name. The top-right guide renders it as a clickable link. */
  actionLabel?: string;
  /** Desktop icon id to pulse. */
  highlight?: string;
  /** Window whose Close button should pulse. */
  closeId?: WinId;
  /** File row to pulse inside Explorer. */
  hotFile?: FileId;
  /** Pulse the Share command once that file is selected. */
  hotShare?: boolean;
  /** Directory card to pulse inside the share dialog. */
  hotEmail?: string;
}

type Fix = { kind: "share"; fileId: FileId } | { kind: "access"; fileId: FileId } | { kind: "apps" };

interface Feedback {
  grade: Grade;
  missionId: MissionId;
  fix: Fix | null;
}

/** Which window the "Fix it" button should open, read from the world rather than from the feedback text. */
function fixTargetFor(id: MissionId, world: World): Fix {
  switch (id) {
    case "m1": {
      const tooMuch =
        !!roleOf(world, "arrival", "morgan-external") ||
        hasAnyoneLink(world, "arrival") ||
        roleOf(world, "arrival", "morgan-internal") === "edit" ||
        roleOf(world, "arrival", "morgan-internal") === "comment";
      return { kind: tooMuch ? "access" : "share", fileId: "arrival" };
    }
    case "m2": {
      const tooMuch =
        world.links.some((l) => l.fileId === "flyer" && l.audience === "anyone" && l.enabled && l.role === "edit") ||
        (hasAnyoneLink(world, "flyer") && !hasAnyoneLink(world, "flyer", "view"));
      return { kind: tooMuch ? "access" : "share", fileId: "flyer" };
    }
    case "m3":
      return { kind: "apps" };
    case "m4": {
      if (roleOf(world, "working", "alex") || hasAnyoneLink(world, "working")) return { kind: "access", fileId: "working" };
      if (hasAnyoneLink(world, "external") || roleOf(world, "external", "alex") === "edit") return { kind: "access", fileId: "external" };
      return { kind: "share", fileId: "external" };
    }
    case "m5":
      return { kind: "share", fileId: "schedule" };
    case "m6":
      return { kind: "access", fileId: "external" };
    default:
      return { kind: "access", fileId: "external" };
  }
}

/** Taskbar and Start entries. Mail and Files are pinned; every other open window gets its own button. */
const TB: Record<string, { label: string; icon: string; tile: string; ic: IcName }> = {
  mail: { label: "Mail", icon: "mail", tile: "#0a84ff", ic: "mail" },
  files: { label: "Files", icon: "files", tile: "#f4b740", ic: "folder" },
  viewer: { label: "Reader", icon: "doc", tile: "#5b9bff", ic: "doc" },
  share: { label: "Share", icon: "share", tile: "#2563a8", ic: "share" },
  access: { label: "Manage access", icon: "access", tile: "#2f7d6d", ic: "lock" },
  guide: { label: "Sharing Guide", icon: "guide", tile: "#0f9d8a", ic: "book" },
  help: { label: "Help", icon: "help", tile: "#7c5cff", ic: "help" },
  apps: { label: "Connected apps", icon: "apps", tile: "#e8833a", ic: "plug" },
};
const TB_ORDER = ["mail", "files", "viewer", "share", "access", "guide", "help", "apps"];
const TB_PINNED = ["mail", "files"];
const START_APPS = ["mail", "files", "guide", "help", "apps"];

const isAppId = (id: string): id is WinId => id in TB;

/** Which file each task is about. Finding it is not the lesson; the permission is. */
const TASK_FILE: Partial<Record<MissionId, FileId>> = {
  m1: "arrival",
  m2: "flyer",
  m4: "external",
  m5: "schedule",
  m6: "external",
};
const CORE_COUNT = 3;


export function SendItSafely({
  onContinue,
  onGuideChange,
}: {
  onContinue: () => void;
  onGuideChange?: (guide: SendItGuide | null) => void;
}) {
  const { wins, open, close, closeAll, focus, minimize, toggleMax, move } = useWindows();
  const compact = useCompact();
  const [mi, setMi] = useState(0);
  const mission = MISSIONS[mi];
  const [world, setWorld] = useState<World>(initialWorld);
  const worldRef = useRef(world);
  const [selected, setSelected] = useState<FileId | null>(null);
  const [viewerFile, setViewerFile] = useState<FileId>("arrival");
  const [shareFile, setShareFile] = useState<FileId>("arrival");
  const [accessFile, setAccessFile] = useState<FileId>("arrival");
  const [readMails, setReadMails] = useState<Set<string>>(() => new Set());
  const [toast, setToast] = useState<{ from: string; subject: string; appId: string; calm?: boolean } | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [whatIf, setWhatIf] = useState(false);
  const [attempts, setAttempts] = useState<Record<string, number>>({});
  const [retryHint, setRetryHint] = useState<string | null>(null);
  const [showRequest, setShowRequest] = useState(false);
  const [committed, setCommitted] = useState<Record<string, boolean>>({});
  const [cancelled, setCancelled] = useState<string[]>([]);

  const setW = useCallback((next: World) => {
    worldRef.current = next;
    setWorld(next);
  }, []);

  // A new request arrives at the start of each task: the toast slides in and Mail shows an unread badge.
  useEffect(() => {
    const t = window.setTimeout(
      () => setToast({ from: mission.mail.from, subject: mission.mail.subject, appId: "mail", calm: mi > 0 }),
      550,
    );
    return () => window.clearTimeout(t);
  }, [mi, mission.mail.from, mission.mail.subject]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 11000);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!startOpen) return;
    const away = (e: PointerEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest(".sis-start") && !el.closest(".win-start")) setStartOpen(false);
    };
    window.addEventListener("pointerdown", away, true);
    return () => window.removeEventListener("pointerdown", away, true);
  }, [startOpen]);

  const activeId = activeWinId(wins);
  const isOpen = (id: WinId) => wins.some((w) => w.id === id && !w.closing);
  const readCurrent = readMails.has(mission.mail.id);

  const openApp = (id: WinId) => {
    open(id);
    setStartOpen(false);
    if (id === "mail") {
      setToast(null);
    }
  };

  const onDeskOpen = (appId: string, via?: "icon" | "taskbar" | "toast") => {
    if (appId === "__start") {
      setStartOpen((v) => !v);
      return;
    }
    if (!isAppId(appId)) return;
    const w = wins.find((x) => x.id === appId && !x.closing);
    if (via === "taskbar" && w && !w.min && activeId === appId) {
      minimize(appId);
      return;
    }
    openApp(appId);
  };

  const openShare = (id: FileId) => {
    setShareFile(id);
    setSelected(id);
    openApp("share");
  };
  const openAccess = (id: FileId) => {
    setAccessFile(id);
    openApp("access");
  };
  const openViewer = (id: FileId) => {
    setViewerFile(id);
    setSelected(id);
    openApp("viewer");
  };

  // ---- grading -----------------------------------------------------------------------------

  const showFeedback = (m: Mission, w: World) => {
    const grade = gradeMission(m.id, w);
    setAttempts((a) => ({ ...a, [m.id]: (a[m.id] ?? 0) + 1 }));
    setCommitted((c) => ({ ...c, [m.id]: true }));
    setFeedback({ grade, missionId: m.id, fix: grade.ok ? null : fixTargetFor(m.id, w) });
    setWhatIf(false);
    setToast(null);
    open("result");
  };

  /** After a fix made once a share has already been judged, only speak up when it now works. */
  const regradeIfPass = (m: Mission, w: World) => {
    if (committed[m.id] && gradeMission(m.id, w).ok) showFeedback(m, w);
  };

  const commitShare = (draft: ShareDraft) => {
    let next = worldRef.current;
    if (draft.mode === "link") {
      next = applyShare(next, { fileId: shareFile, audience: "anyone", role: draft.role });
    } else {
      for (const g of draft.groups) {
        next = applyShare(next, { fileId: shareFile, audience: "group", groupId: g, role: draft.role });
      }
      for (const p of draft.people) {
        const external = personOf(p)?.org !== "northstar";
        next = applyShare(next, {
          fileId: shareFile,
          audience: "person",
          personId: p,
          role: draft.role,
          expiresAtDone: draft.until === "done",
          asGuest: draft.guest && external,
        });
      }
    }
    setW(next);
    close("share");
    window.setTimeout(() => showFeedback(mission, next), 230);
  };

  const removeRow = (row: AccessRow) => {
    if (!row.removeId) return;
    let next = worldRef.current;
    if (row.kind === "guest") next = removeGuest(next, row.removeId);
    else if (row.kind === "link" || row.kind === "named-link") next = removeLink(next, row.removeId);
    else next = removeGrant(next, row.removeId);
    setW(next);
    if (mission.id !== "m6") regradeIfPass(mission, next);
  };

  const requestLocation = () => {
    const next = moveScheduleToProject(worldRef.current);
    setW(next);
    if (mission.id === "m3") regradeIfPass(mission, next);
  };

  const confirmClosed = () => showFeedback(mission, worldRef.current);

  const decideApps = (next: World, nextCancelled: string[]) => {
    setW(next);
    setCancelled(nextCancelled);
    if (mission.id !== "m3") return;
    const broadApproved = next.apps.some((a) => a.appId === "broad-viewer" && !a.revoked);
    const allDecided = APP_REQUESTS.every((a) => appStatus(next, nextCancelled, a.id) !== "pending");
    if (broadApproved || allDecided) window.setTimeout(() => showFeedback(mission, next), 280);
  };
  const approveRequest = (id: string) =>
    decideApps(
      approveApp(worldRef.current, id, id === "workshop-helper" ? "selected-doc" : "broad"),
      cancelled.filter((x) => x !== id),
    );
  const cancelRequest = (id: string, report: boolean) => {
    let next = revokeApp(worldRef.current, id);
    if (report) next = reportApp(next, id);
    decideApps(next, cancelled.includes(id) ? cancelled : [...cancelled, id]);
  };
  const removeRequest = (id: string) => decideApps(revokeApp(worldRef.current, id), cancelled);
  const reportRequest = (id: string) => decideApps(reportApp(worldRef.current, id), cancelled);

  // ---- flow --------------------------------------------------------------------------------

  const startMission = (n: number) => {
    closeAll();
    setStartOpen(false);
    setToast(null);
    setSelected(null);
    setWhatIf(false);
    setCancelled([]);
    setRetryHint(null);
    setShowRequest(false);
    setAccessFile(MISSIONS[n].id === "m6" ? "external" : "arrival");
    if (MISSIONS[n].id === "m6") setW({ ...worldRef.current, clock: "done" });
    setMi(n);
  };

  const onFix = () => {
    const fix = feedback?.fix;
    const grade = feedback?.grade;
    const missionId = feedback?.missionId;
    close("result");
    if (!fix || !grade || !missionId) return;
    setRetryHint(`${grade.why} ${grade.next}`);
    window.setTimeout(() => {
      if (fix.kind === "apps") {
        openApp("apps");
        return;
      }
      if (missionId === "m6") {
        openAccess(fix.fileId);
        return;
      }
      const files: FileId[] = missionId === "m4" ? ["working", "external"] : [fix.fileId];
      const shareFileId: FileId = missionId === "m4" ? "external" : fix.fileId;
      setW(files.reduce((w, id) => clearDirectShares(w, id), worldRef.current));
      close("access");
      openShare(shareFileId);
    }, 200);
  };

  const onNext = () => startMission(mi + 1);
  const onMore = () => startMission(CORE_COUNT);

  const onSummary = () => {
    close("result");
    window.setTimeout(() => open("summary"), 210);
  };

  const backToTerminal = () => {
    const ret = new URLSearchParams(window.location.search).get("return");
    if (ret && /^[a-z0-9-]{1,64}$/.test(ret)) {
      window.location.href = hubBackHref(ret);
      return;
    }
    onContinue();
  };

  // ---- Mail --------------------------------------------------------------------------------

  const mailConfig = useMemo<MailConfig>(() => {
    const handled = new Set(MISSIONS.slice(0, mi).map((m) => m.mail.id));
    const requests = MISSIONS.slice(0, mi + 1)
      .map((m) => ({
        id: m.mail.id,
        from: m.mail.from,
        fromAddress: m.mail.fromAddress,
        to: "you",
        subject: m.mail.subject,
        preview: m.request.length > 76 ? `${m.request.slice(0, 74)}\u2026` : m.request,
        receivedAt: m.mail.receivedAt,
        unread: !readMails.has(m.mail.id) && !handled.has(m.mail.id),
        badge: handled.has(m.mail.id) ? "Done" : undefined,
        initials: m.mail.initials,
        body: m.mail.body,
      }))
      .reverse();
    return {
      appTitle: "Outlook",
      emptyTitle: "Select a message to read it",
      emptyHint: `Open the new message from ${mission.mail.from}.`,
      emails: requests,
    };
  }, [mi, mission.mail.from, readMails]);

  const onOpenEmail = useCallback((id: string) => {
    setReadMails((cur) => (cur.has(id) ? cur : new Set(cur).add(id)));
  }, []);

  // ---- desktop shell -----------------------------------------------------------------------

  const openIds = wins.filter((w) => !w.closing && isAppId(w.id)).map((w) => w.id as string);
  const tbIds = TB_ORDER.filter((id) => TB_PINNED.includes(id) || openIds.includes(id));
  const tbKey = tbIds.join("|");

  const deskConfig = useMemo<DesktopConfig>(() => {
    const app = (id: string): DesktopApp => ({ id, label: TB[id].label, icon: TB[id].icon, opens: id });
    return {
      clock: mission.clock,
      date: mission.date,
      icons: ["files", "mail", "guide", "help", "apps"].map(app),
      taskbar: tbKey.split("|").map((id) => ({ ...app(id), id: `tb_${id}` })),
    };
  }, [mission.clock, mission.date, tbKey]);

  // ---- top-right guide ---------------------------------------------------------------------

  const resultOpen = isOpen("result");
  const summaryOpen = isOpen("summary");
  const shareOpen = isOpen("share");
  const guide = useMemo<SendItGuide>(() => {
    const taskTotal = mi >= CORE_COUNT ? MISSIONS.length : CORE_COUNT;
    const step = `Task ${mi + 1} of ${taskTotal} · ${mission.title}`;
    const target = TASK_FILE[mission.id];
    const say = (title: string, body: string, extra?: Partial<SendItGuide>): SendItGuide => ({
      step,
      title,
      body,
      ...extra,
    });
    if (summaryOpen) {
      return { step: "Finished", title: "Back to the terminal", body: "All six tasks are done. Go back to the terminal when you are ready for the quiz.", actionLabel: "Back to the terminal" };
    }
    if (resultOpen && feedback?.grade.ok && feedback.missionId === "m3" && mi < CORE_COUNT) {
      return say("Three tasks done", "The required tasks are finished. Choose Do three more, or Back to the terminal.", { actionLabel: "Do three more" });
    }
    if (resultOpen) {
      const ok = feedback?.grade.ok;
      return say(
        ok ? "That access was right" : "That access needs a change",
        ok ? "Read what happened. Then choose Continue." : "Read what happened. Then choose Fix it and try the access again.",
        { actionLabel: ok ? "Continue" : "Fix it" },
      );
    }
    if (retryHint) {
      return say("Try again", retryHint);
    }
    if (!readCurrent) {
      if (!isOpen("mail")) {
        return say("Open Mail", "A new request has arrived. Open Mail.", { actionLabel: "Mail", highlight: "mail" });
      }
      return say("Read the request", `Select “${mission.mail.subject}” and read who needs the file, and what they need to do with it.`, { actionLabel: mission.mail.subject });
    }
    if (isOpen("mail")) {
      return say("Close Mail", "You have the request. Close Mail so you can work on the file.", { actionLabel: "Close", closeId: "mail" });
    }
    if (mission.id === "m3") {
      if (!isOpen("apps")) {
        return say("Open Connected apps", "Open Connected apps and decide each request.", { actionLabel: "Connected apps", highlight: "apps" });
      }
      return say("Decide each app", "Decide what each tool is allowed to do. The result appears after both requests are decided.");
    }
    if (mission.id === "m6") {
      if (!isOpen("files") && !isOpen("access")) {
        return say("Open Files", "Open Files. The document for this task is marked This task.", { actionLabel: "Files", highlight: "files" });
      }
      if (!isOpen("access") && selected !== "external") {
        return say("Choose the document", `Select “${fileOf("external").name}”. It is marked This task.`, { actionLabel: fileOf("external").name, hotFile: "external" });
      }
      if (!isOpen("access")) {
        return say("Open Manage access", "Open Manage access and remove the access the email says should end.", { actionLabel: "Manage access" });
      }
      return say("Close leftover access", "Remove access that should end. Leave what the team still needs, then confirm.", { actionLabel: "Confirm access is closed" });
    }
    if (mission.id === "m5" && shareOpen && placeOf(world, "schedule") === "shared-folder") {
      return say("Move the schedule", "The schedule is still in the shared folder. Press Request a restricted project location, then choose who can edit it.", { actionLabel: "Request a restricted project location" });
    }
    if (isOpen("access")) {
      return say("Review who has access", "This window is who can open the file now. Change only what the request requires. The result appears after you confirm.");
    }
    if (!isOpen("files")) {
      return say("Open Files", "Open Files. The document for this task is marked This task.", { actionLabel: "Files", highlight: "files" });
    }
    if (target && selected !== target) {
      return say("Choose the document", `Select “${fileOf(target).name}”. It is marked This task.`, { actionLabel: fileOf(target).name, hotFile: target });
    }
    if (!shareOpen) {
      return say("Open Share", "Open Share. You will choose who gets access and what they can do.", { actionLabel: "Share", hotShare: true });
    }
    return say("Choose the access", "Choose who gets the file and what they can do, then press Share.");
  }, [mi, mission.id, mission.title, mission.mail.subject, summaryOpen, resultOpen, readCurrent, shareOpen, selected, feedback, wins, retryHint, world]);

  const guideKey = `${guide.step}|${guide.title}|${guide.body}|${guide.actionLabel ?? ""}`;
  useEffect(() => {
    onGuideChange?.(guide);
  }, [guideKey, onGuideChange]); // eslint-disable-line -- guideKey stands for the whole guide object
  useEffect(() => () => onGuideChange?.(null), [onGuideChange]);

  // ---- windows -----------------------------------------------------------------------------

  const titleOf = (id: WinId): string => {
    switch (id) {
      case "mail":
        return "Outlook";
      case "files":
        return "Workshop \u2013 File Explorer";
      case "viewer":
        return `${fileOf(viewerFile).name} \u2013 Reader`;
      case "share":
        return `Share \u2013 ${fileOf(shareFile).name}`;
      case "access":
        return `Manage access \u2013 ${fileOf(accessFile).name}`;
      case "guide":
        return "Rules for this simulation";
      case "help":
        return "Help";
      case "apps":
        return "Connected apps";
      case "result":
        return resultTitle(feedback);
      case "summary":
        return "Workshop wrap-up";
    }
  };

  const body = (meta: WinMeta) => {
    switch (meta.id) {
      case "mail":
        return <Mail config={mailConfig} onOpenEmail={onOpenEmail} />;
      case "files":
        return (
          <FilesWin
            world={world}
            selected={selected}
            hotFileId={guide.hotFile}
            hotShare={!!guide.hotShare}
            taskFileId={TASK_FILE[mission.id]}
            doneFileIds={MISSIONS.slice(0, mi).flatMap((m) => {
              const id = TASK_FILE[m.id];
              return id && id !== TASK_FILE[mission.id] ? [id] : [];
            })}
            onSelect={setSelected}
            onOpen={openViewer}
            onShare={openShare}
            onManage={openAccess}
          />
        );
      case "viewer":
        return <ViewerBody fileId={viewerFile} onShare={() => openShare(viewerFile)} onManage={() => openAccess(viewerFile)} />;
      case "share":
        return (
          <ShareDialog
            key={shareFile}
            fileId={shareFile}
            world={world}
            onCancel={() => close("share")}
            onCommit={commitShare}
            onManage={() => openAccess(shareFile)}
            onRequestLocation={requestLocation}
            highlightEmail={guide.hotEmail}
          />
        );
      case "access":
        return (
          <AccessWin
            world={world}
            fileId={accessFile}
            missionId={mission.id}
            onFile={setAccessFile}
            onRemove={removeRow}
            onRequestLocation={requestLocation}
            onConfirm={confirmClosed}
            onShare={openShare}
          />
        );
      case "guide":
        return <GuideBody />;
      case "help":
        return <HelpBody onAccess={() => openAccess(accessFile)} onApps={() => openApp("apps")} />;
      case "apps":
        return (
          <AppsWin
            world={world}
            cancelled={cancelled}
            onApprove={approveRequest}
            onCancel={cancelRequest}
            onRemove={removeRequest}
            onReport={reportRequest}
            onGuide={() => openApp("guide")}
          />
        );
      case "result":
        return feedback ? (
          <ResultBody
            grade={feedback.grade}
            taskNo={MISSIONS.findIndex((m) => m.id === feedback.missionId) + 1}
            total={mi >= CORE_COUNT ? MISSIONS.length : CORE_COUNT}
            missionId={feedback.missionId}
            last={feedback.missionId === "m6"}
            offerMore={feedback.missionId === "m3" && feedback.grade.ok && mi < CORE_COUNT}
            onMore={onMore}
            onDone={backToTerminal}
            whatIf={whatIf}
            onWhatIf={() => setWhatIf((v) => !v)}
            onFix={onFix}
            onContinue={onNext}
            onSummary={onSummary}
          />
        ) : null;
      case "summary":
        return (
          <SummaryBody
            rows={MISSIONS.map((m) => ({ id: m.id, title: m.title, first: attempts[m.id] === 1 }))}
            onBack={backToTerminal}
          />
        );
    }
  };

  const renderWin = (meta: WinMeta, i: number) => {
    const modal = meta.id === "result" || meta.id === "summary";
    const win = (
      <SisWin
        key={meta.id}
        meta={meta}
        z={10 + i}
        active={activeId === meta.id}
        compact={compact}
        title={titleOf(meta.id)}
        modal={modal}
        onFocus={() => focus(meta.id)}
        onClose={() => close(meta.id)}
        pulseClose={guide.closeId === meta.id}
        onMin={() => minimize(meta.id)}
        onMax={() => toggleMax(meta.id)}
        onMove={(x, y) => move(meta.id, x, y)}
      >
        {body(meta)}
      </SisWin>
    );
    if (!modal) return win;
    return (
      <div key={meta.id} className={`sis-modal${meta.closing ? " is-closing" : ""}`} style={{ zIndex: 10 + i }}>
        {win}
      </div>
    );
  };

  return (
    <Desktop
      className="sis-desk"
      config={deskConfig}
      onOpen={onDeskOpen}
      highlightAppId={guide.highlight}
      toast={toast}
      openAppIds={openIds}
      activeAppId={activeId}
      badgeAppIds={readCurrent ? [] : ["mail"]}
    >
      <div className="sis-layer">
        {wins.map(renderWin)}
        <div className={`sis-coach${wins.some((w) => !w.min && !w.closing) ? " sis-coach--rail" : ""}`} role="status">
          <span className="sis-coach-k">{guide.step}</span>
          <strong>{guide.title}</strong>
          <p>{guide.body}</p>
          {(shareOpen || isOpen("apps") || isOpen("access") || !!retryHint) && !summaryOpen && !resultOpen && (
            <button type="button" className="sis-mailbtn" onClick={() => setShowRequest(true)}>
              <Ic n="mail" size={16} /> Need help?
            </button>
          )}
        </div>
        {showRequest && (shareOpen || isOpen("apps") || isOpen("access") || !!retryHint) && !resultOpen && (
          <div className="sis-mailpop" role="dialog" aria-label="The request email" onClick={() => setShowRequest(false)}>
            <div className="sis-mailpop-card" onClick={(e) => e.stopPropagation()}>
              <p className="sis-mailpop-from">{mission.mail.from} · {mission.mail.fromAddress}</p>
              <h3>{mission.mail.subject}</h3>
              {mission.mail.body.split("\n").filter((line) => line.trim()).map((line) => (
                <p key={line}>{line}</p>
              ))}
              <button type="button" className="sis-mailbtn" onClick={() => setShowRequest(false)}>
                Close
              </button>
            </div>
          </div>
        )}
        {startOpen && (
          <div className="sis-start" role="menu" aria-label="Start">
            <div className="sis-start-h">Pinned</div>
            <div className="sis-start-grid">
              {START_APPS.map((id, i) => (
                <button
                  key={id}
                  type="button"
                  role="menuitem"
                  className="sis-start-app"
                  style={{ animationDelay: `${i * 28}ms` }}
                  onClick={() => openApp(id as WinId)}
                >
                  <span className="sis-start-tile" style={{ background: TB[id].tile }}>
                    <Ic n={TB[id].ic} size={20} />
                  </span>
                  <span>{TB[id].label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Desktop>
  );
}

function resultTitle(feedback: Feedback | null): string {
  if (!feedback) return "Result";
  return feedback.grade.ok ? "Result \u2013 done" : "Result \u2013 needs a fix";
}
